import 'swiper/css';

import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import MyHead from '../../components/MyHead';
import Image from 'next/future/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import StaticPagesCards from '@/components/v2/StaticPagesCards';
import VideoPlayer from '@/components/v2/VideoPlayer';
import LinkTo from '@/components/LinkTo';
import { buttonVariants } from '@/components/v2/UI/Button';

const SustainabilityPage: React.FC = () => {
  const { t } = useTranslation();

  const cardsOne = [
    {
      title: t('P_SUSTAINABILITY.CARDNAV-1.CARD-1.TITLE'),
      description: t('P_SUSTAINABILITY.CARDNAV-1.CARD-1.DESC'),
      icosrc: '/v2/sustainability/icon-secondlife.png',
      link: '/second-life',
      btntext: t('P_SUSTAINABILITY.CARDNAV-1.CARD-1.BTN'),
      src: '/v2/sustainability/sus1.jpg'
    },
    {
      title: t('P_SUSTAINABILITY.CARDNAV-1.CARD-2.TITLE'),
      description: t('P_SUSTAINABILITY.CARDNAV-1.CARD-2.DESC'),
      icosrc: '/v2/sustainability/icon-biomaterials.png',
      link: '/materials',
      btntext: t('P_SUSTAINABILITY.CARDNAV-1.CARD-1.BTN'),
      src: '/v2/sustainability/sus2.jpg'
    },
    {
      title: t('P_SUSTAINABILITY.CARDNAV-1.CARD-3.TITLE'),
      description: t('P_SUSTAINABILITY.CARDNAV-1.CARD-3.DESC'),
      icosrc: '/v2/sustainability/icon-airnest.png',
      link: '/airnest',
      btntext: t('P_SUSTAINABILITY.CARDNAV-1.CARD-1.BTN'),
      src: '/v2/sustainability/sus3.jpg'
    }
  ];
  const cardsTwo = [
    {
      description: t('P_SUSTAINABILITY.CARDNAV-2.CARD-1.DESC'),
      icosrc: '/v2/sustainability/oekotex-logo.png'
    },
    {
      description: t('P_SUSTAINABILITY.CARDNAV-2.CARD-2.DESC'),
      icosrc: '/v2/sustainability/global-recycled-logo.png'
    },
    {
      description: t('P_SUSTAINABILITY.CARDNAV-2.CARD-3.DESC'),
      icosrc: '/v2/sustainability/oekotex-green-logo.png'
    }
  ];

  return (
    <Fragment>
      <MyHead />

      <div className="section relative full bg-[#D6DFD9]">
        <div className="section-wrapper relative text-white">
          <div className="grid gap-8 grid-cols-12 md:gap-12 xl:gap-20">
            <div className="md:col-span-6 col-span-12">
              <div className="lead-2 text-[#3B817A]">
                {t('P_SUSTAINABILITY.SECTION-1.TITLE')}
              </div>
              <div className="text-[#3B817A] tracking-uyn md:text-xl opacity-80 mt-6">
                {t('P_SUSTAINABILITY.SECTION-1.HASH')}
              </div>
            </div>
            <div className="md:col-span-4 md:col-start-8 col-span-8 col-start-3">
              <Image
                src="/v2/sustainability/Logo-committed.png"
                alt=""
                width={0}
                height={0}
                priority
                sizes="100vw"
                className="w-full"
              />
            </div>

            <div className="col-span-12 video-wrapper aspect-landscape">
              <VideoPlayer
                src="https://vimeo.com/847042760/8548b1a143"
                autoPlay
              />
            </div>

            <div className="md:col-span-6 col-span-12">
              <div className="h2 text-[#3B817A]">
                {t('P_SUSTAINABILITY.SECTION-1.TITLE-2')}
              </div>
              <div className="text-[#3B817A] tracking-uyn md:text-xl opacity-80 mt-6">
                {t('P_SUSTAINABILITY.SECTION-1.DESC')}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="section relative">
        <div className="section-wrapper relative">
          <div className="grid gap-8 grid-cols-12">
            <div className="md:col-span-6 col-span-12 relative h-fit">
              <div className="video-wrapper aspect-square w-full max-w-[65%] ml-auto z-20">
                <VideoPlayer
                  src="https://vimeo.com/847240852/30cc0a4678"
                  autoPlay
                />
              </div>
              <Image
                src="/v2/sustainability/water-1.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full max-w-[50%] mt-[-35%] relative z-30"
              />
              <Image
                src="/v2/sustainability/water-2.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full max-w-[50%] mt-[-35%] mx-auto relative z-10"
              />
            </div>

            <div className="md:col-span-5 md:col-start-8 col-span-12 text-[#92CACF] space-y-16 self-center">
              <div>
                <div className="lead-2">
                  {t('P_SUSTAINABILITY.SECTION-2.TITLE')}
                </div>
                <div className="tracking-uyn md:text-xl uppercase mt-2">
                  {t('P_SUSTAINABILITY.SECTION-2.DESC')}
                </div>
              </div>
              <div>
                <div className="h3">
                  {t('P_SUSTAINABILITY.SECTION-2.TITLE-2')}
                </div>
                <div className="text-body mt-2">
                  {t('P_SUSTAINABILITY.SECTION-2.DESC-2')}
                </div>
              </div>
              <div>
                <div className="h3">
                  {t('P_SUSTAINABILITY.SECTION-2.TITLE-3')}
                </div>
                <div className="text-body mt-2">
                  {t('P_SUSTAINABILITY.SECTION-2.DESC-3')}
                </div>
              </div>
              <div>
                <div className="h3">
                  {t('P_SUSTAINABILITY.SECTION-2.TITLE-4')}
                </div>
                <div className="text-body mt-2">
                  {t('P_SUSTAINABILITY.SECTION-2.DESC-4')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="section relative">
        <div className="section-wrapper relative">
          <div className="grid gap-8 grid-cols-12">
            <div className="md:col-span-5 col-span-12 text-[#538AA0] space-y-16 self-center order-2 md:order-1">
              <div>
                <div className="lead-2">
                  {t('P_SUSTAINABILITY.SECTION-3.TITLE')}
                </div>
                <div className="tracking-uyn md:text-xl uppercase mt-2">
                  {t('P_SUSTAINABILITY.SECTION-3.DESC')}
                </div>
              </div>
              <div>
                <div className="h3">
                  {t('P_SUSTAINABILITY.SECTION-3.TITLE-2')}
                </div>
                <div className="text-body mt-2">
                  {t('P_SUSTAINABILITY.SECTION-3.DESC-2')}
                </div>
              </div>
              <div>
                <div className="h3">
                  {t('P_SUSTAINABILITY.SECTION-3.TITLE-3')}
                </div>
                <div className="text-body mt-2">
                  {t('P_SUSTAINABILITY.SECTION-3.DESC-3')}
                </div>
              </div>
              <div>
                <div className="h3">
                  {t('P_SUSTAINABILITY.SECTION-3.TITLE-4')}
                </div>
                <div className="text-body mt-2">
                  {t('P_SUSTAINABILITY.SECTION-3.DESC-4')}
                </div>
              </div>
            </div>

            <div className="md:col-span-6 md:col-start-7 col-span-12 relative h-fit order-1 md:order-2">
              <Image
                src="/v2/sustainability/air-1.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full max-w-[75%] relative z-20"
              />
              <div className="video-wrapper aspect-square max-w-[50%] ml-auto mt-[-35%] z-30">
                <VideoPlayer
                  src="https://vimeo.com/847249010/30b4467681"
                  autoPlay
                />
              </div>
              <Image
                src="/v2/sustainability/air-2.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full max-w-[50%] mt-[-35%] mx-auto relative z-10"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="section relative">
        <div className="section-wrapper relative">
          <div className="grid gap-8 grid-cols-12">
            <div className="md:col-span-6 col-span-12 relative h-fit">
              <Image
                src="/v2/sustainability/earth-1.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full max-w-[65%] ml-auto mr-[5%] relative z-10"
              />
              <Image
                src="/v2/sustainability/earth-2.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full max-w-[50%] mt-[-35%] relative z-30"
              />
              <Image
                src="/v2/sustainability/earth-3.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full max-w-[75%] mt-[-35%] ml-auto relative z-20"
              />
            </div>

            <div className="md:col-span-5 md:col-start-8 col-span-12 text-[#6FA184] space-y-16 self-center">
              <div>
                <div className="lead-2">
                  {t('P_SUSTAINABILITY.SECTION-4.TITLE')}
                </div>
                <div className="tracking-uyn md:text-xl uppercase mt-2">
                  {t('P_SUSTAINABILITY.SECTION-4.DESC')}
                </div>
              </div>
              <div>
                <div className="h3">
                  {t('P_SUSTAINABILITY.SECTION-4.TITLE-2')}
                </div>
                <div className="text-body mt-2">
                  {t('P_SUSTAINABILITY.SECTION-4.DESC-2')}
                </div>
              </div>
              <div>
                <div className="h3">
                  {t('P_SUSTAINABILITY.SECTION-4.TITLE-3')}
                </div>
                <div className="text-body mt-2">
                  {t('P_SUSTAINABILITY.SECTION-4.DESC-3')}
                </div>
              </div>
              <div>
                <div className="h3">
                  {t('P_SUSTAINABILITY.SECTION-4.TITLE-4')}
                </div>
                <div className="text-body mt-2">
                  {t('P_SUSTAINABILITY.SECTION-4.DESC-4')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="section relative full">
        <div className="section-wrapper relative">
          <Swiper
            wrapperTag="ul"
            slidesPerView={1.2}
            spaceBetween={16}
            className=""
            breakpoints={{
              768: {
                slidesPerView: 2.2
              },
              1024: {
                slidesPerView: 3
              }
            }}
          >
            {cardsOne.map((card, idx) => (
              <SwiperSlide
                key={idx}
                tag="li"
                className="group rounded-uyn overflow-hidden relative aspect-cardSwiper"
              >
                <Image
                  src={card.src}
                  alt=""
                  width={0}
                  height={0}
                  sizes="100vw"
                  className="absolute w-full h-full object-cover object-center"
                />

                <div className="absolute z-10 w-full h-full left-0 top-0 bg-black/40 group-hover:bg-black/50 transition-all"></div>
                <div className="absolute z-20 inset-0 flex flex-col text-white p-6 md:p-8">
                  <Image
                    src={card.icosrc}
                    alt=""
                    width={0}
                    height={0}
                    sizes="100vw"
                    className="w-full max-w-[120px] h-auto"
                  />
                  <div className="mt-[25%]">
                    <h3 className="h4">{card.title}</h3>
                    <p className="text-body">{card.description}</p>
                  </div>

                  <LinkTo
                    href={card.link}
                    aria-label={card.btntext}
                    className={buttonVariants({
                      variant: 'light',
                      className: 'mt-auto md:w-max'
                    })}
                  >
                    {card.btntext}
                  </LinkTo>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      <div className="section relative full bg-[#B0C2B6]">
        <div className="section-wrapper relative text-white">
          <div className="grid gap-8 grid-cols-12">
            <div className="md:col-span-5 col-span-12 self-center">
              <div className="lead-2">
                {t('P_SUSTAINABILITY.SECTION-5.TITLE')}
              </div>
              <div className="h5 mt-6">
                {t('P_SUSTAINABILITY.SECTION-5.HASH')}
              </div>
              <div className="text-body mt-6">
                {t('P_SUSTAINABILITY.SECTION-5.DESC')}
              </div>
            </div>

            <div className="md:col-span-7 col-span-12">
              <Image
                src="/v2/sustainability/how2.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full"
              />
            </div>

            <div className="md:col-span-6 col-span-12 pt-16">
              <div className="h2">
                {t('P_SUSTAINABILITY.SECTION-5.TITLE-2')}
              </div>
              <div className="tracking-uyn md:text-xl opacity-80 mt-6">
                {t('P_SUSTAINABILITY.SECTION-5.DESC-2')}
              </div>
            </div>

            <div className="col-span-12">
              <Swiper
                wrapperTag="ul"
                slidesPerView={1.2}
                spaceBetween={16}
                className=""
                breakpoints={{
                  768: {
                    slidesPerView: 2.2
                  },
                  1024: {
                    slidesPerView: 3
                  }
                }}
              >
                {cardsTwo.map((card, idx) => (
                  <SwiperSlide
                    key={idx}
                    tag="li"
                    className="rounded-uyn overflow-hidden relative bg-[#9BACA1]"
                  >
                    <div className="flex flex-col text-white p-6 md:p-8">
                      <Image
                        src={card.icosrc}
                        alt=""
                        width={0}
                        height={0}
                        sizes="100vw"
                        className="max-h-[90px] w-fit"
                      />
                      <div className="mt-16">
                        <p className="text-body">{card.description}</p>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </div>

      <StaticPagesCards />
    </Fragment>
  );
};

export default SustainabilityPage;
